import { useEffect, useState } from 'react';
import { Elements, useStripe } from '@stripe/react-stripe-js';
import { useNavigate, useSearchParams } from 'react-router';
import { Box, Typography, CircularProgress, Alert, Button, Container } from '@mui/material';
import { toast } from 'sonner';
import { stripePromise } from '@/util/stripe';
import { getBuyPanelsPath } from '../route';

/**
 * PaymentReturn component handles the asynchronous PayTo (and other redirect-based) payment flows.
 * 
 * When a customer approves a PayTo mandate in their banking app, they are redirected here.
 * The component then polls the payment status until either:
 * - Payment succeeded → redirect to confirmation page
 * - Payment failed → show error and allow retry
 * - Timeout → show waiting message and allow manual check
 */
// Internal component that runs inside the Stripe Elements context
function PaymentReturnContent(): JSX.Element {
  const stripe = useStripe();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'checking' | 'processing' | 'success' | 'error' | 'timeout'>('checking');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [pollCount, setPollCount] = useState(0);
  const [manualCheckEnabled, setManualCheckEnabled] = useState(false);

  const clientSecret = searchParams.get('payment_intent_client_secret');
  const orderFromUrl = searchParams.get('order') ?? undefined;
  const MAX_POLLS = 20;
  const POLL_INTERVAL = 3000; 
  const routePrefix = getBuyPanelsPath(window.location.pathname, '');

  // Simple animated loading dots used for polling UI
  function LoadingDots({ interval = 500, max = 3 }: { interval?: number; max?: number }) {
    const [count, setCount] = useState(0);
    useEffect(() => {
      const id = setInterval(() => setCount(c => (c + 1) % (max + 1)), interval);
      return () => clearInterval(id);
    }, [interval, max]);
    return <span style={{ letterSpacing: 2 }}>{'.'.repeat(count)}</span>;
  }

  const isTokenExpired = (token: string) => {
    try {
      const parts = token.split('.');
      if (parts.length < 2) return true;
      const payload = JSON.parse(atob(parts[1]));
      if (!payload || !payload.exp) return true;
      return payload.exp * 1000 < Date.now();
    } catch (e) {
      return true;
    }
  };

  const handleCheckStatus = async (isManual = false) => {
    if (!stripe || !clientSecret) {
      if (!clientSecret) {
        setStatus('error');
        setErrorMessage('Payment intent not found. Please try again.');
      }
      return;
    }

    try {
      const { paymentIntent } = await stripe.retrievePaymentIntent(clientSecret);

      if (!paymentIntent) {
        setStatus('error');
        setErrorMessage('Unable to retrieve payment status.');
        return;
      }

      switch (paymentIntent.status) {
        case 'succeeded':
          setStatus('success');
          toast.success('Payment successful!');
          const metadata = (paymentIntent as any)?.metadata as Record<string, any> | undefined;
          const orderUuid = orderFromUrl ?? metadata?.orderUuid ?? metadata?.order_uuid;

          // Inform backend to finalise the order in case the Stripe webhook hasn't been delivered yet
          if (orderUuid) {
            (async () => {
              try {
                let token = "";
                for (let i = 0; i < localStorage.length; i++) {
                  const key = localStorage.key(i);
                  if (key && (key.endsWith('idToken') || key.endsWith('accessToken'))) {
                    token = localStorage.getItem(key) || "";
                    break;
                  }
                }
                
                // Normalize token and check expiry
                let finalizationSucceeded = false;
                const cleanToken = token ? token.replace(/\"/g, '') : '';
                const hasValidToken = cleanToken && !isTokenExpired(cleanToken);

                if (hasValidToken) {
                  try {
                    const response = await fetch(`/api/orders/new/${encodeURIComponent(orderUuid)}/action/post-payment/stripe`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${cleanToken}`
                      },
                      body: JSON.stringify({ paymentIntentId: paymentIntent.id })
                    });
                    finalizationSucceeded = response.ok;
                    if (!finalizationSucceeded) console.warn('Authenticated finalization returned', response.status);
                  } catch (err) {
                    console.warn('Authenticated post-payment request failed, trying unauthenticated', err);
                  }
                }

                // Try unauthenticated confirmation endpoint as a fallback (verifies with Stripe server-side)
                if (!finalizationSucceeded) {
                  try {
                    // Use GET query variant to avoid JSON body deserialization issues on the server
                    const response = await fetch(`/api/orders/new/${encodeURIComponent(orderUuid)}/action/post-payment/stripe/noauth?paymentIntentId=${encodeURIComponent(paymentIntent.id)}`, {
                      method: 'GET',
                      headers: { 'Accept': 'application/json' }
                    });
                    finalizationSucceeded = response.ok;
                  } catch (e) {
                    console.warn('Unauthenticated post-payment request also failed; will poll order status and rely on webhook', e);
                  }
                }
                
                // Poll by re-calling finalization endpoint (idempotent) to avoid side effects from GET /new/{uuid}
                let pollCount = 0;
                const maxPolls = 20; // ~10 seconds with 500ms intervals
                while (pollCount < maxPolls && !finalizationSucceeded) {
                  try {
                    if (hasValidToken) {
                      const response = await fetch(`/api/orders/new/${encodeURIComponent(orderUuid)}/action/post-payment/stripe`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${cleanToken}`
                        },
                        body: JSON.stringify({ paymentIntentId: paymentIntent.id })
                      });
                      finalizationSucceeded = response.ok;
                    } else {
                      const response = await fetch(`/api/orders/new/${encodeURIComponent(orderUuid)}/action/post-payment/stripe/noauth?paymentIntentId=${encodeURIComponent(paymentIntent.id)}`, {
                        method: 'GET',
                        headers: { 'Accept': 'application/json' }
                      });
                      finalizationSucceeded = response.ok;
                    }
                  } catch (err) {
                    console.warn('Finalization poll attempt failed:', err);
                  }

                  if (finalizationSucceeded) {
                    break;
                  }

                  await new Promise(resolve => setTimeout(resolve, 500));
                  pollCount++;
                }
                
                if (!finalizationSucceeded) {
                  console.warn('Order status did not update within polling timeout; continuing anyway as webhook may still process');
                }
              } catch (err) {
                console.error('Unexpected error in payment finalization:', err);
              }
              
              // Navigate to confirmation regardless - webhook will eventually process if not already done
              setTimeout(() => {
                if (orderUuid) {
                  navigate(`${routePrefix}/confirmation?order=${encodeURIComponent(orderUuid)}`);
                } else {
                  navigate(`${routePrefix}/confirmation`, { state: { paymentIntentId: paymentIntent.id } });
                }
              }, 500);
            })();
          } else {
            // No order UUID - navigate to confirmation after brief delay
            setTimeout(() => {
              navigate(`${routePrefix}/confirmation`, { state: { paymentIntentId: paymentIntent.id } });
            }, 1500);
          }
          break;

        case 'processing':
          if (!isManual) {
            setStatus('processing');
            setPollCount(prev => {
              const newPoll = prev + 1;
              if (newPoll >= MAX_POLLS) {
                setStatus('timeout');
                setManualCheckEnabled(true);
              } else {
                setTimeout(() => handleCheckStatus(), POLL_INTERVAL);
              }
              return newPoll;
            });
          }
          break;

        case 'requires_payment_method':
        case 'requires_action':
          setStatus('error');
          setErrorMessage('Payment authorization failed. Please try a different payment method.');
          toast.error('Payment authorization failed. Please try again.');
          break;

        default:
          setStatus('error');
          setErrorMessage(`Unexpected payment status: ${paymentIntent.status}`);
          break;
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
      setErrorMessage('Unable to check payment status. Please try again.');
    }
  };

  useEffect(() => {
    if (stripe && clientSecret) {
      handleCheckStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripe, clientSecret]);

  const handleRetry = () => {
    navigate(`${routePrefix}/payment`);
  };

  const handleManualCheck = () => {
    setPollCount(0);
    setManualCheckEnabled(false);
    setStatus('checking');
    handleCheckStatus(true);
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ textAlign: 'center', py: 8, color: '#fff' }}>
        {status === 'checking' && (
          <>
            <CircularProgress sx={{ mb: 2 }} size={48} />
            <Typography variant="h6" sx={{ mb: 1, color: '#fff' }}>
              Verifying Payment
            </Typography>
            <Typography sx={{ mb: 3, color: '#fff' }}>
              Please wait while we confirm your payment details...
            </Typography>
          </>
        )}

        {status === 'processing' && (
          <>
            <CircularProgress sx={{ mb: 2 }} size={48} />
            <Typography variant="h6" sx={{ mb: 1, color: '#fff' }}>
              Bank is Authorizing Your Payment
            </Typography>
            <Typography sx={{ mb: 3, color: '#fff' }}>
              PayTo authorization can take up to 60 seconds. Your bank is confirming the transaction.
            </Typography>
            <Typography variant="body2" sx={{ color: '#fff', mt: 1 }}>
              <LoadingDots />
            </Typography>
          </>
        )}

        {status === 'success' && (
          <>
            <Typography variant="h6" sx={{ mb: 2, color: 'success.main' }}>
              ✓ Payment Successful
            </Typography>
            <Typography sx={{ mb: 3, color: '#fff' }}>
              Your payment has been confirmed. Redirecting to confirmation page...
            </Typography>
            <CircularProgress size={32} />
          </>
        )}

        {status === 'timeout' && (
          <>
            <Alert severity="warning" sx={{ mb: 3, textAlign: 'left' }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Payment Still Processing
              </Typography>
              <Typography variant="body2">
                Your bank is still processing the authorization. This can take a few more minutes.
                You can check the status manually below, or you'll receive an email confirmation once complete.
              </Typography>
            </Alert>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleManualCheck}
              sx={{ mr: 1 }}
            >
              Check Status Again
            </Button>
            <Button 
              variant="outlined" 
              onClick={() => navigate(routePrefix)}
            >
              Return to Orders
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Payment Failed
              </Typography>
              <Typography variant="body2">
                {errorMessage || 'An unexpected error occurred during payment processing.'}
              </Typography>
            </Alert>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={handleRetry}
              >
                Try Again
              </Button>
              <Button 
                variant="outlined" 
                onClick={() => navigate(routePrefix)}
              >
                Cancel
              </Button>
            </Box>
          </>
        )}

        {manualCheckEnabled && status === 'timeout' && (
          <Typography variant="caption" sx={{ display: 'block', mt: 3, color: '#fff' }}>
            If you've been waiting for more than a few minutes, your bank may require additional verification.
            Please check your banking app or contact your bank directly.
          </Typography>
        )}
      </Box>
    </Container>
  );
}

// Default export wraps the content in the required Stripe provider
export default function PaymentReturn(): JSX.Element {
  return (
    <Elements stripe={stripePromise}>
      <PaymentReturnContent />
    </Elements>
  );
}
